import React, { useState, useCallback } from 'react';
import {
  Toolbar,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import {
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  toolbarIconGroupSx,
} from './utils/gridToolbarParts.js';
import { CustomQuickFilter } from './utils/CustomQuickFilter.js';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PushPinIcon from '@mui/icons-material/PushPin';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WorkIcon from '@mui/icons-material/Work';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  toolbarIconButtonSx,
  toolbarIconProps,
  toolbarButtonBaseSx,
  toolbarOutlinedButtonSx,
  toolbarDividerSx,
  toolbarMenuPaperSx,
  toolbarMenuSx,
  toolbarMenuIconSx,
} from './utils/toolbarKit.js';
import CompSearch, { compSearchMuniCode } from './CompSearch';
import SettlementButton from './Settlement/SettlementButton';
import SubjectDataGridRow from './SubjectDataGridRow';
import { toast } from '../../common/toast';
import { regenerateWorkups } from '../../services/regenerateWorkupsService.js';
import SvgIconButton from './SvgIconButton';
import { useUser, getUserNameById, WarnList } from '../UserContext';
import UnifiedCancelDialog from './CancelOffers/UnifiedCancelDialog';
import VacateStipDialog from './CancelOffers/VacateStipDialog.js';
import { useCancelOffers } from './CancelOffers/useCancelOffers';
import WorkupViewerDialog from './Workups/WorkupVisualization/WorkupViewerDialog.js';
import NyscefUploadModal from './NyscefUpload/NyscefUploadModal.js';
import DocRequestDialog from './SummaryCaseTable/components/DocRequestDialog.js';
import DocRequestChip, { CompletedDocRequestChip } from './SummaryCaseTable/components/DocRequestChip.js';
import { usePersistedDataStore } from '../store';
import logger from '../../utils/logger';
import { normalizeCompId, selectionKeyForRow } from './common/compSheetLookup';

const disabledMenuItemSx = { opacity: 0.38, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } };

// PDF Report Generator component
const PDFReportGenerator = ({ subject, userId, typeOutput = 'onepager', label = 'Download One Pager', isLoading, onLoadingChange, disabled, onClose }) => {
  // pass in this parcel id to the generator
  logger.log('subject', subject.parcel_id);

  const handlePrintCompReport = async () => {
    onClose?.();
    try {
      const subjectParcelId = subject?.parcel_id;
      if (!subjectParcelId) {
        toast.error("No subject parcel ID found.");
        onLoadingChange?.(false);
        return;
      }
      
      onLoadingChange?.(true);
      
      // Determine which report types to generate based on typeOutput
      // For onepager: generate onepagermaps
      // For fnmaSummary: fnmaSummary
      const reportTypes = typeOutput === 'fnmaSummary' 
        ? ['fnmaSummary']
        : ['onepagermaps'];

      // const fileName = `${subject.PropertyInfo.Address} ${reportType === 'standard' ? 'Sales' : 'Assessment'} Comp Report`
      
      await regenerateWorkups({
        parcelIds: [subjectParcelId],
        reportTypes,
        userId,
        download: true,
        // fileName,
      });
      
      // Update loading state when regeneration completes
      onLoadingChange?.(false);
    } catch (error) {
      logger.error("Error regenerating and downloading workup:", error);
      onLoadingChange?.(false);
    }
  };

  return (
    <MenuItem onClick={handlePrintCompReport} disabled={disabled || isLoading}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        {label}
        {isLoading && (
          <CircularProgress size={16} sx={{ ml: 1 }} />
        )}
      </Box>
    </MenuItem>
  );
};

// Custom CSV Export Menu Item with loading state
const CustomCsvExportMenuItem = ({ subject, isLoading, onLoadingChange, disabled, selectedRows = [], onClose, isSaleRun = false }) => {
  const apiRef = useGridApiContext();

  const handleCsvExport = async () => {
    onClose?.();
    try {
      onLoadingChange(true);
      // Use a small delay to ensure the loading state is visible
      await new Promise(resolve => setTimeout(resolve, 100));
      // If no rows are selected, export all rows; otherwise export only selected rows
      const hasSelectedRows = selectedRows && selectedRows.length > 0;
      const isPlaceholderRow = (row) => row?.id === 'new-comp';
      const getRowsToExport = () => {
        const allRowIds = apiRef.current.getSortedRowIds?.() || apiRef.current.getAllRowIds?.() || [];
        const selectedSet = hasSelectedRows
          ? new Set(selectedRows.map(normalizeCompId).filter(Boolean))
          : null;
        return allRowIds.filter((id) => {
          const row = apiRef.current.getRow?.(id);
          if (isPlaceholderRow(row)) return false;
          if (!selectedSet) return true;
          return selectedSet.has(normalizeCompId(id))
            || selectedSet.has(selectionKeyForRow(row, isSaleRun));
        });
      };
      apiRef.current.exportDataAsCsv({
        fileName: `${subject?.PropertyInfo?.Address || ""}_comps`,
        delimiter: ',',
        allRows: !hasSelectedRows,
        getRowsToExport
      });
    } catch (error) {
      logger.error("Error exporting CSV:", error);
      toast.error("Error exporting CSV.");
    } finally {
      // Add a small delay before hiding loading to ensure export completes
      setTimeout(() => {
        onLoadingChange(false);
      }, 500);
    }
  };

  return (
    <MenuItem onClick={handleCsvExport} disabled={disabled || isLoading}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        Download Comp CSV
        {isLoading && (
          <CircularProgress size={16} sx={{ ml: 1 }} />
        )}
      </Box>
    </MenuItem>
  );
};

// Custom Export Button component
export const CustomExportButton = ({ subject, compNumber, selectedRows, userId, isSaleRun = false }) => {
  const [loadingType, setLoadingType] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isLoading = loadingType !== null;
  const label = isLoading ? 'Downloading' : 'Download';

  const handleLoadingChange = (type, loading) => {
    setLoadingType(loading ? type : null);
  };

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <SvgIconButton
        icon={isLoading ? (props) => <CircularProgress size={18} {...props} /> : DownloadIcon}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        tooltip={label}
        sx={toolbarIconButtonSx}
        iconProps={toolbarIconProps}
        activeColor={open ? 'primary.main' : 'text.secondary'}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={label}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: toolbarMenuPaperSx } }}
        sx={toolbarMenuSx}
      >
        <PDFReportGenerator
          subject={subject}
          userId={userId}
          typeOutput="onepager"
          label="Download One Pager"
          isLoading={loadingType === 'onepager'}
          onLoadingChange={(loading) => handleLoadingChange('onepager', loading)}
          disabled={isLoading && loadingType !== 'onepager'}
          onClose={closeMenu}
        />
        <PDFReportGenerator
          subject={subject}
          userId={userId}
          typeOutput="fnmaSummary"
          label="Download FNMA"
          isLoading={loadingType === 'fnmaSummary'}
          onLoadingChange={(loading) => handleLoadingChange('fnmaSummary', loading)}
          disabled={isLoading && loadingType !== 'fnmaSummary'}
          onClose={closeMenu}
        />
        <CustomCsvExportMenuItem
          subject={subject}
          selectedRows={selectedRows}
          isSaleRun={isSaleRun}
          isLoading={loadingType === 'csv'}
          onLoadingChange={(loading) => handleLoadingChange('csv', loading)}
          disabled={isLoading && loadingType !== 'csv'}
          onClose={closeMenu}
        />
      </Menu>
    </>
  );
};


// Main shared toolbar component
const SharedGridToolbar = React.memo(({
  // Common props
  filteredHeaders,
  subject,
  updateRefWithSubj,
  handleSetPropertyInfo,
  unsavedChangesRef,
  columnVisibilityModel,
  columnLayout = null,
  
  // Optional props for different use cases
  showSettlement = false,
  caseObject = null,
  onSettlementClick = null,
  onSettlementComplete = null,
  isSettled = false,
  
  // DataGridContainer specific props
  setIsPinned = null,
  selectedRows = [],
  isSaleRun = false,
  userName = '',
  compNumber = 0,
  setIsSavingCallback = null,
  /** Invoked as handleSaveComps(ids); second-arg saveOpts are supplied by the parent callback (e.g. NegotiationTable). */
  handleSaveComps = null,
  saveChanges = null,
  userUpdate = null,
  discardChanges = null,
  handleToggleReordering = null,
  reordering = false,
  addCompCallback = null,
  userReordered = false,
  isPinned = false,
  reOrderingRef = null,
  isSaving = false,
  hideToolbar = false,
  onManualRefresh = null,
  /** When true (NegotiationTable only): Save uses townNegotiationComps row order instead of userUpdate / reorder ref */
  useTownNegotiationCompsForSave = false,
  /** Comp parcel_ids in negotiation grid row order; only used if useTownNegotiationCompsForSave */
  townNegotiationComps = [],
  
}) => {
  const caseReviewToolbarRev = usePersistedDataStore((s) => s.caseReviewToolbarRevision);
  const isFetching = usePersistedDataStore((s) => s.isFetching);
  const [refreshingComps, setRefreshingComps] = useState(false);

  const { user } = useUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const [caseMenuAnchor, setCaseMenuAnchor] = useState(null);
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [pendingIdsToSave, setPendingIdsToSave] = useState([]);
  const [previousReviewerName, setPreviousReviewerName] = useState('');
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [initialWorkupType, setInitialWorkupType] = useState('sales');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [docRequestDialogOpen, setDocRequestDialogOpen] = useState(false);
  
  // Use unified cancellation hook
  const {
    cancelDialogOpen,
    cancellingOffers,
    openCancelDialog,
    closeCancelDialog,
    handleConfirmCancelSingle,
    getNegotiationObj
  } = useCancelOffers();

  const subjectParcelId = caseObject?.parcel_id || subject?.parcel_id;
  const compMuniCode = compSearchMuniCode(getNegotiationObj?.MuniCode, subjectParcelId);
  const uploadRows = subjectParcelId ? [{ parcel_id: subjectParcelId }] : [];

  const handleRefreshComps = async () => {
    if (!onManualRefresh || refreshingComps || isFetching) return;
    setRefreshingComps(true);
    try { await onManualRefresh(); } finally { setRefreshingComps(false); }
  };

  const handleDocumentIconClick = () => {
    // Determine initial workup type based on report type
    const reportType = getNegotiationObj?.reportType;
    setInitialWorkupType(reportType === 'assessment' ? 'equity' : 'sales');
    setDocumentViewerOpen(true);
  };

  const handleDocumentViewerClose = () => {
    setDocumentViewerOpen(false);
  };

  // Add comp popover
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  // Case actions menu
  const caseMenuOpen = Boolean(caseMenuAnchor);
  const closeCaseMenu = () => setCaseMenuAnchor(null);
  const runCaseAction = (fn) => () => {
    closeCaseMenu();
    fn();
  };

  const handlePinToggle = useCallback(() => {
    if (setIsPinned) {
      setIsPinned(prev => {
        const newValue = !prev;
        localStorage.setItem('pinnedComps', JSON.stringify(newValue));
        return newValue;
      });
    }
  }, [setIsPinned]);

  // Cancel settlement handler
  const handleCancelSettlement = () => {
    openCancelDialog();
  };

  const handleConfirmCancel = async () => {
    await handleConfirmCancelSingle(
      caseObject?.parcel_id,
      caseObject?.Address || caseObject?.parcel_id
    );
  };

  // Check if user is a training user
  const isTrainingUser = user?.userAccess?.level === 'Training';

  const determinationAction = (caseObject?.MasterPetition === 1 || caseObject?.MasterPetition === true)
    ? caseObject?.Art7DeterminationAction
    : caseObject?.SCARDeterminationAction;
  const awaitingDecision = !isSettled && String(determinationAction ?? '').trim() === 'D';

  const hasUnsavedChanges = (() => {
    if (!unsavedChangesRef?.current) return false;

    // Check if unsavedRows has any entries with actual changes
    const hasUnsavedRows = Object.keys(unsavedChangesRef.current.unsavedRows).some(pid => {
      const changes = unsavedChangesRef.current.unsavedRows[pid];
      return changes && Object.keys(changes).length > 0 && Object.keys(changes).some(key => key !== 'pid');
    });

    // Check if unsavedSubject has any actual changes
    const hasUnsavedSubject = Object.keys(unsavedChangesRef.current.unsavedSubject).length > 0 &&
      Object.keys(unsavedChangesRef.current.unsavedSubject).some(key => key !== 'pid');

    return hasUnsavedRows || hasUnsavedSubject;
  })();

  const settleDisabledReason = isSettled ? 'Case is settled' : (isTrainingUser ? 'Not available for training accounts' : '');
  const pinColor = isPinned ? 'primary.main' : 'text.secondary';
  const showReorder = !hasUnsavedChanges;
  const showSelectionGroup = Boolean(setIsPinned) || showReorder;

  const handleSaveClick = () => {
    let idsToSave;
    if (useTownNegotiationCompsForSave) {
      idsToSave = townNegotiationComps;
    } else if (reordering && userReordered) {
      idsToSave = reOrderingRef?.current;
      handleToggleReordering && handleToggleReordering();
    } else {
      idsToSave = userUpdate?.length > 0 ? userUpdate : [];
    }
    const userId = user?.userId;
    // Warn via MUI dialog if overwriting comps saved by a protected user
    try {
      const previousReviewerId = caseObject?.ReviewedBy || caseObject?.reviewedBy;
      const wasManuallyReviewed = Boolean(caseObject?.ManualReview);
      if (
        previousReviewerId &&
        wasManuallyReviewed &&
        Array.isArray(WarnList) &&
        userId !== previousReviewerId &&
        WarnList.includes(Number(previousReviewerId))
      ) {
        const prevName = getUserNameById?.(previousReviewerId) || `User ${previousReviewerId}`;
        setPreviousReviewerName(prevName);
        setPendingIdsToSave(idsToSave);
        setOverwriteDialogOpen(true);
        return;
      }
    } catch (e) {}
    setIsSavingCallback && setIsSavingCallback();
    if (handleSaveComps) {
      handleSaveComps(idsToSave);
    } else {
      logger.warn('[SharedGridToolbar] Save Comps clicked but handleSaveComps is not provided; save skipped.');
    }
  };

  // Don't render anything if toolbar is hidden
  if (hideToolbar) {
    return null;
  }
  return (
    <>
    <Toolbar data-case-review-toolbar-rev={caseReviewToolbarRev} sx={{ justifyContent: 'space-between', width: '100%', py: 0.375, minHeight: 46 }}>
      <div className='flex items-center justify-between w-full'>

        {/* ── Left: grid-level tools ── */}
        <div className='flex items-center'>
          <Box className='toolbar-buttons' sx={toolbarIconGroupSx}>
            <GridToolbarColumnsButton columnLayout={columnLayout} />
            <GridToolbarFilterButton />
            <CustomExportButton compNumber={compNumber} selectedRows={selectedRows} userId={user?.userId} subject={subject} isSaleRun={isSaleRun} />
            {onManualRefresh && (
              <SvgIconButton
                icon={refreshingComps ? (props) => <CircularProgress size={18} {...props} /> : RefreshIcon}
                onClick={handleRefreshComps}
                tooltip="Refresh comps"
                disabled={refreshingComps || isFetching}
                sx={toolbarIconButtonSx}
                iconProps={toolbarIconProps}
                aria-label="refresh comps"
              />
            )}
            <CustomQuickFilter debounceMs={200} />
          </Box>
        </div>

        {/* ── Right: case status, comp actions, case menu, save ── */}
        <div className='flex-shrink-0 flex items-center justify-end'>
          <DocRequestChip requests={caseObject?.DocRequests} label="Open Doc Request" />
          <CompletedDocRequestChip requests={caseObject?.DocRequests} />
          {/* Settlement Status Indicator */}
          {showSettlement && awaitingDecision && (
            <div className="px-3 py-1 bg-amber-100 text-amber-900 rounded-md text-sm font-medium mr-2">
              Awaiting Decision
            </div>
          )}
          {showSettlement && isSettled && (
            <div className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm font-medium mr-2 flex items-center gap-2">
              <span>{caseObject?.SCARDeterminationAction === 'W' || caseObject?.BARDeterminationAction === 'W' ? 'Case Withdrawn' : 'Case Settled'}</span>
              <Tooltip title="Cancel Settlement">
                <span className="inline-flex">
                  <IconButton
                    size="small"
                    onClick={handleCancelSettlement}
                    disabled={cancellingOffers}
                    sx={{
                      padding: '2px',
                      minWidth: 'auto',
                      width: '20px',
                      height: '20px',
                      color: '#dc2626',
                      '&:hover': {
                        backgroundColor: '#fef2f2',
                        color: '#b91c1c'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

            {showSettlement && (
              <SettlementButton
                caseObject={caseObject}
                onSettlementClick={onSettlementClick}
                onSettlementComplete={onSettlementComplete}
                disabledReason={settleDisabledReason}
                buttonSx={toolbarIconButtonSx}
                iconProps={toolbarIconProps}
              />
            )}

            {addCompCallback && (
              <>
                <SvgIconButton
                  icon={AddIcon}
                  onClick={handleClick}
                  tooltip="Add property by Address or Parcel ID"
                  sx={toolbarIconButtonSx}
                  iconProps={toolbarIconProps}
                  aria-describedby={id}
                  aria-label="Add comp"
                  activeColor={open ? 'primary.main' : 'text.secondary'}
                />
                <Popover
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <div className='p-4 relative overflow-visible'>
                    <CompSearch
                      handleClose={handleClose}
                      compSelectCallback={addCompCallback}
                      searchData={[]}
                      restrictToState="NY"
                      muniCode={compMuniCode}
                    />
                  </div>
                </Popover>
              </>
            )}

            {/* Selected-comp actions: pin + reorder */}
            {showSelectionGroup && (
              <>
                {setIsPinned && (
                  <SvgIconButton
                    icon={PushPinIcon}
                    onClick={handlePinToggle}
                    tooltip="Pin selected comps to top"
                    sx={toolbarIconButtonSx}
                    iconProps={{ sx: { ...toolbarIconProps.sx, transform: 'rotate(-45deg)' } }}
                    activeColor={pinColor}
                    hoverColor={pinColor}
                  />
                )}

                {showReorder && (
                  reordering ? (
                    <SvgIconButton
                      icon={userReordered ? CheckIcon : CloseIcon}
                      onClick={handleToggleReordering}
                      tooltip={userReordered ? "Confirm reordering" : "Cancel reordering"}
                      sx={toolbarIconButtonSx}
                      iconProps={toolbarIconProps}
                      activeColor={userReordered ? 'success.main' : 'text.secondary'}
                      hoverColor={userReordered ? 'success.main' : 'error.main'}
                    />
                  ) : (
                    <SvgIconButton
                      icon={SwapVertIcon}
                      onClick={handleToggleReordering}
                      tooltip="Reorder selected comps"
                      sx={toolbarIconButtonSx}
                      iconProps={toolbarIconProps}
                      disabled={selectedRows.length === 0}
                      activeColor="text.secondary"
                    />
                  )
                )}
              </>
            )}

            <Divider orientation="vertical" flexItem sx={toolbarDividerSx} />

            {/* Case actions menu */}
            <Button
              variant="outlined"
              startIcon={<WorkIcon sx={toolbarMenuIconSx} />}
              endIcon={<KeyboardArrowDownIcon sx={toolbarMenuIconSx} />}
              onClick={(e) => setCaseMenuAnchor(e.currentTarget)}
              aria-haspopup="true"
              aria-expanded={caseMenuOpen ? 'true' : undefined}
              aria-controls={caseMenuOpen ? 'case-actions-menu' : undefined}
              sx={toolbarOutlinedButtonSx(caseMenuOpen)}
            >
              Docs
            </Button>
            <Menu
              id="case-actions-menu"
              anchorEl={caseMenuAnchor}
              open={caseMenuOpen}
              onClose={closeCaseMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: toolbarMenuPaperSx } }}
              sx={toolbarMenuSx}
            >
              <MenuItem onClick={runCaseAction(handleDocumentIconClick)}>
                <ListItemIcon><DescriptionIcon sx={toolbarMenuIconSx} /></ListItemIcon>
                <ListItemText primary="View documents" />
              </MenuItem>

              {subjectParcelId && (
                <MenuItem onClick={runCaseAction(() => setUploadDialogOpen(true))}>
                  <ListItemIcon><UploadFileIcon sx={toolbarMenuIconSx} /></ListItemIcon>
                  <ListItemText primary="Upload to NYSCEF" />
                </MenuItem>
              )}

              {/* aria-disabled instead of disabled: disabled MenuItems block pointer events, which kills the tooltip */}
              {caseObject?.parcel_id && (
                <Tooltip title={isSettled ? 'Case is settled' : ''} placement="left">
                  <MenuItem
                    onClick={isSettled ? undefined : runCaseAction(() => setDocRequestDialogOpen(true))}
                    aria-disabled={isSettled}
                    disableRipple={isSettled}
                    sx={isSettled ? disabledMenuItemSx : undefined}
                  >
                    <ListItemIcon><ReportProblemIcon sx={toolbarMenuIconSx} /></ListItemIcon>
                    <ListItemText primary="Jurisdictional doc request" />
                  </MenuItem>
                </Tooltip>
              )}
            </Menu>

            {/* Save comps, or Discard / Update when there are unsaved edits */}
            {hasUnsavedChanges ? (
              <div className='px-0.5'>
                <Button
                  variant='outlined'
                  className='mr-2 hover:border-red-500 hover:text-red-500'
                  onClick={() => discardChanges && discardChanges()}
                  sx={toolbarButtonBaseSx}
                >
                  Discard
                </Button>
                <Button
                  variant='outlined'
                  className='hover:border-green-600 hover:text-green-600'
                  onClick={saveChanges}
                  sx={toolbarButtonBaseSx}
                >
                  Update
                </Button>
              </div>
            ) : (
              <Button 
                disabled={isSaving}
                variant="outlined"
                onClick={handleSaveClick}
                endIcon={isSaving ? <CircularProgress size={16} /> : null}
                sx={{
                  ...toolbarButtonBaseSx,
                  width: '120px',
                  ml: 0.5,
                  ...(isSaving && {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                  })
                }}
              >
                {isSaving ? 'Saving' : 'Save Comps'}
              </Button>
            )}
          </Box>
        </div>
      </div>
    </Toolbar>

      <div className="flex-shrink-0">
        <SubjectDataGridRow
          subject={subject}
          updateRefWithSubj={updateRefWithSubj}
          handleSetPropertyInfo={handleSetPropertyInfo}
          unsavedChangesRef={unsavedChangesRef}
          headers={filteredHeaders}
          columnVisibilityModel={columnVisibilityModel}
        />
      </div>

      <VacateStipDialog />
      {/* Cancel Settlement Dialog */}
      <UnifiedCancelDialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        rowsToCancel={caseObject ? [{
          parcel_id: caseObject.parcel_id,
          Address: caseObject.Address || caseObject.parcel_id
        }] : []}
        cancellingOffers={cancellingOffers}
        onConfirm={handleConfirmCancel}
        getNegotiationObj={getNegotiationObj}
      />

      {/* Overwrite Warning Dialog */}
      <StableMuiDialog open={overwriteDialogOpen} onClose={() => setOverwriteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Overwrite Saved Comps?</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
            These comps were previously saved by {previousReviewerName || 'another user'}. Overwrite with your current selection?
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOverwriteDialogOpen(false)} sx={{ fontSize: '0.9rem' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const idsToSave = Array.isArray(pendingIdsToSave) ? pendingIdsToSave : [];
              setOverwriteDialogOpen(false);
              setIsSavingCallback && setIsSavingCallback();
              if (handleSaveComps) {
                handleSaveComps(idsToSave);
              } else {
                logger.warn('[SharedGridToolbar] Overwrite save but handleSaveComps is not provided; save skipped.');
              }
            }}
            sx={{ fontSize: '0.9rem' }}
          >
            Overwrite
          </Button>
        </DialogActions>
      </StableMuiDialog>

      {/* Document Viewer Dialog */}
      <WorkupViewerDialog
        open={documentViewerOpen}
        onClose={handleDocumentViewerClose}
        files={caseObject?.S3Files || []}
        nyscefFiles={caseObject?.NyscefS3Files || []}
        address={caseObject?.Address || ''}
        scarIndexNumber={caseObject?.SCARIndexNumber || ''}
        initialWorkupType={initialWorkupType}
      />

      <NyscefUploadModal
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        selectedRows={uploadRows}
        rows={uploadRows}
      />

      <DocRequestDialog
        open={docRequestDialogOpen}
        onClose={() => setDocRequestDialogOpen(false)}
        rows={caseObject ? [caseObject] : []}
        negotiationObj={getNegotiationObj}
      />
    </>
  );
});

SharedGridToolbar.displayName = 'SharedGridToolbar';

export default SharedGridToolbar;
